/**
 * Phase 1 · Shared Album Presentation Model.
 *
 * Single source of truth for how a collector's album is named,
 * categorised and summarised across My Profile, My Collection, Hall
 * of Fame and the album detail page. All customer-facing rules live
 * here — never rebuild these in individual pages.
 *
 * Accepts either the dashboard-summary card shape or the enriched
 * my-collection album shape and returns:
 *
 *   {
 *     officialTitle,           // e.g. "FIFA World Cup 2026 Official Sticker Collection"
 *     coverLabel,              // e.g. "Hardcover - United Edition" (never a raw slug)
 *     nickname,                // trimmed collector nickname or ""
 *     collectionMethod,        // raw enum value from the DB (unmodified)
 *     collectionMethodLabel,   // customer-facing label
 *     tracksProgress,          // boolean — false for Collection Only
 *     status,                  // "collection_only" | "in_progress" | "completed"
 *     statusLabel,             // "Collection Only" | "In Progress" | "Completed"
 *     progressPercent,         // integer 0..100 or null when !tracksProgress
 *     ownedCount, missingCount, duplicateCount,
 *     checklistSize,
 *     isCompleted,
 *     isCollectionOnly,
 *     openAlbumHref,           // routing anchor, never surfaced as text
 *   }
 *
 * NEVER emits: "Sealed", "Sealed Collection", "Ownership tracked",
 *              raw catalog slugs as titles.
 */

import { wcCoverLabelForVariant } from "@/lib/wcCoverLabels";

// Customer-facing official titles by release slug or year. Reuses
// existing release metadata; falls back to the payload's own title.
const OFFICIAL_TITLE_BY_RELEASE_SLUG = {
  wc26: "FIFA World Cup 2026 Official Sticker Collection",
};

// Collection-method enum → customer-facing behaviour.
// The enum values are `applied_to_album`, `binder` (a.k.a. `sticker`),
// `paper` (a.k.a. `binder`/`sticker` legacy alias), and `album_only`.
// Only album_only is Collection-Only.
const COLLECTION_METHOD_META = {
  applied_to_album: { label: "Applied to Album", tracks: true },
  sticker:          { label: "Stored in Binder", tracks: true },
  binder:           { label: "Stored in Binder", tracks: true },
  paper:            { label: "Stored in Binder", tracks: true },
  album_only:       { label: "Collection Only",  tracks: false },
};

// The default is used only for TYPE inference (percent gating,
// tracksProgress) when the album has no explicit method. It MUST NOT
// leak "Applied to Album" as a visible label — callers should render
// `collectionMethodLabel` only when non-null. See `hasCollectionMethodLabel`.
const DEFAULT_METHOD = { label: null, tracks: true };

/**
 * A1 · Collection Method Consistency.
 *
 * The ONE label helper every surface (My Collection, My Profile,
 * Public Collector Profile → Album Library, Public Album Detail)
 * must use. Never invent per-component `METHOD_LABELS` maps.
 *
 * Accepts either a raw album row or a presenter result. Returns
 * `null` when no meaningful method exists — callers should hide the
 * "Collection Method" row in that case rather than rendering a
 * placeholder or a raw enum string.
 */
export function collectionMethodLabelFor(albumOrPresentation) {
  if (!albumOrPresentation) return null;
  // Presenter result — already resolved.
  if (typeof albumOrPresentation.collectionMethodLabel !== "undefined") {
    return albumOrPresentation.collectionMethodLabel || null;
  }
  const key = albumOrPresentation.collection_method || null;
  if (!key) return null;
  const meta = COLLECTION_METHOD_META[key];
  return meta ? meta.label : null;
}

export const COLLECTION_ONLY_MESSAGE = "Stored empty without stickers applied.";

export const STATUS_LABELS = {
  collection_only: "Collection Only",
  in_progress:     "In Progress",
  completed:       "Completed",
};

// ────────────────────────────────────────────────────────────────
// Field readers — accept the dashboard card, my-collection album,
// and the album-detail row shapes with a single API.
// ────────────────────────────────────────────────────────────────
function readReleaseSlug(album) {
  if (!album) return null;
  const explicit =
    album.release?.slug ||
    album.catalog?.release?.slug ||
    // Historical Album Card Title Fix — dashboard-summary cards carry
    // the release slug on a flat field so the frontend title resolver
    // can identify historical WC releases without the full catalog
    // object.
    album.release_slug ||
    (album.public_slug && album.public_slug.match(/wc26/i) ? "wc26" : null);
  if (explicit) return explicit;
  // Fallback detection for the my-collection album shape, where
  // `release.slug` is not populated but the variant code carries the
  // release prefix (e.g. `WC26-US-SC`, `WC26-CH`, `WC26-GOLD`).
  const variant = album.album_variant || album.catalog?.album_variant || null;
  if (variant?.code && /^WC26(\b|[-_])/i.test(variant.code)) return "wc26";
  const year =
    album.release?.year ||
    album.catalog?.release?.year ||
    null;
  if (year === 2026 && variant?.code && /^WC26/i.test(variant.code)) return "wc26";
  return null;
}

// Historical Album Card Title Fix — resolve a historical FIFA World
// Cup title of the form "{Year} {Host} FIFA World Cup Collection"
// when we know both year AND host. Returns null when either signal
// is absent or the release is the current edition (wc26, which keeps
// its canonical "FIFA World Cup 2026 Official Sticker Collection").
function readHistoricalWcTitle(album) {
  const slug = readReleaseSlug(album);
  if (slug === "wc26") return null;
  const release = album?.release || album?.catalog?.release || {};
  const year =
    release.year ||
    album?.tournament_year ||
    null;
  const host =
    release.host_country ||
    album?.release_host_country ||
    null;
  if (!year || !host) return null;
  // Only apply when the release slug looks like a WC (wc22, wc18, wc14, wc10, …).
  if (slug && !/^wc\d{2,4}$/i.test(slug)) return null;
  return `${year} ${host} FIFA World Cup Collection`;
}

// Historical Album Card Title Fix — normalise softcover / hardcover
// edition to the customer-facing labels "Softcover" / "Hardcover".
// Returns null when we cannot confidently classify the edition.
function readHistoricalWcCoverLabel(album) {
  const slug = readReleaseSlug(album);
  if (slug === "wc26") return null;
  const variant = album?.album_variant || album?.catalog?.album_variant || null;
  const edition =
    variant?.edition_type ||
    album?.variant_edition_type ||
    null;
  if (!edition) return null;
  const e = String(edition).toLowerCase();
  if (e === "softcover") return "Softcover";
  if (e === "hardcover") return "Hardcover";
  return null;
}

function readOfficialTitle(album) {
  // Historical Album Card Title Fix — historical FIFA World Cup
  // releases (WC22, WC18, WC14, WC10, …) render as
  // "{Year} {Host} FIFA World Cup Collection" per owner directive.
  // WC26 is intentionally excluded; keep the canonical title.
  const hist = readHistoricalWcTitle(album);
  if (hist) return hist;
  // 1. Prefer the release's own `official_title` when the enriched
  //    catalog payload provides one — this is populated by the
  //    catalog seed for every WC edition (WC26, WC22, WC10, …).
  const release = album?.release || album?.catalog?.release || {};
  const canonical = String(release.official_title || "").trim();
  if (canonical) return canonical;
  // 2. Legacy fallback — the hardcoded alias map (only used when the
  //    payload predates the `official_title` seed).
  const slug = readReleaseSlug(album);
  if (slug && OFFICIAL_TITLE_BY_RELEASE_SLUG[slug]) {
    return OFFICIAL_TITLE_BY_RELEASE_SLUG[slug];
  }
  // 3. Fall back to the release's public display name — NEVER the raw slug.
  const t = release.public_display_name || release.name;
  if (t) return t;
  // Dashboard-card shape carries `subtitle` = release name.
  if (album?.subtitle) return album.subtitle;
  return "Album";
}

function readCoverLabel(album) {
  if (!album) return "";
  // Override takes absolute precedence — respects "Other", the United
  // Edition rename, and any future custom label.
  if (album.cover_label_override) return String(album.cover_label_override).trim();
  // Historical Album Card Title Fix — normalise historical WC editions
  // to "Softcover" / "Hardcover" so the subtitle never repeats the
  // year + host already surfaced by the primary title.
  const hist = readHistoricalWcCoverLabel(album);
  if (hist) return hist;
  const variant = album.album_variant || album.catalog?.album_variant || null;
  if (variant) return wcCoverLabelForVariant(variant);
  // Dashboard-card shape places the variant's public label under `title`.
  return album.title || "";
}

function readNickname(album) {
  return String(album?.nickname || "").trim();
}

function readCounts(album) {
  const c = album?.completion || album?.summary || {};
  // Dashboard-summary payload uses `owned_effective`; my-collection
  // payload uses `owned`. Read both for full compatibility.
  const owned = Number(
    c.owned ?? c.owned_effective ?? album?.owned ?? 0
  );
  const missing = Number(c.missing ?? album?.missing ?? 0);
  const duplicate = Number(c.duplicate ?? album?.duplicate ?? 0);
  const checklistSize = Number(
    c.total_stickers ?? c.checklist_size ?? album?.checklist_size ?? 0
  );
  return { owned, missing, duplicate, checklistSize };
}

function readPercent(album) {
  const c = album?.completion || album?.summary;
  if (c && typeof c === "object") {
    const p = Number(c.completion_percentage);
    if (Number.isFinite(p)) return clamp(p);
  }
  const flat = Number(album?.completion_percentage);
  if (Number.isFinite(flat)) return clamp(flat);
  return null;
}

function clamp(n) { return n < 0 ? 0 : n > 100 ? 100 : n; }

// ────────────────────────────────────────────────────────────────
// Main entry point
// ────────────────────────────────────────────────────────────────
export function buildAlbumPresentation(album) {
  if (!album || typeof album !== "object") {
    return {
      officialTitle: "Album", coverLabel: "", nickname: "",
      collectionMethod: null, collectionMethodLabel: DEFAULT_METHOD.label,
      tracksProgress: true,
      status: "in_progress", statusLabel: STATUS_LABELS.in_progress,
      progressPercent: 0, ownedCount: 0, missingCount: 0, duplicateCount: 0,
      checklistSize: 0, isCompleted: false, isCollectionOnly: false,
      openAlbumHref: null,
    };
  }

  const method = album.collection_method || null;
  const meta = COLLECTION_METHOD_META[method] || DEFAULT_METHOD;
  const tracksProgress = meta.tracks;

  const counts = readCounts(album);
  const percentRaw = readPercent(album);
  const isCompleted =
    tracksProgress && (
      album.status === "completed" ||
      album?.completion?.is_complete === true ||
      (percentRaw !== null && percentRaw >= 100)
    );

  let status;
  if (!tracksProgress) status = "collection_only";
  else if (isCompleted)  status = "completed";
  else                   status = "in_progress";

  return {
    officialTitle: readOfficialTitle(album),
    coverLabel:    readCoverLabel(album),
    nickname:      readNickname(album),
    collectionMethod: method,
    collectionMethodLabel: meta.label,
    tracksProgress,
    status,
    statusLabel: STATUS_LABELS[status],
    // Phase C2a · Completed-album read-time normalization.
    //
    // When an album is marked completed AND we know a canonical
    // checklist size, we surface a self-consistent completion view:
    // 100% progress, `collected = checklistSize`, `missing = 0`.
    // This removes the previously possible contradiction where a
    // completed album could display "Completed 0/992 0%". Raw
    // inventory rows are NOT modified — only the presentation model.
    //
    // Duplicate counts are preserved verbatim: they represent extra
    // copies available for trade beyond the single album copy and
    // must never be forced to zero.
    progressPercent: tracksProgress
      ? (isCompleted && counts.checklistSize > 0
          ? 100
          : Math.round(percentRaw === null ? 0 : percentRaw))
      : null,
    ownedCount:     tracksProgress
      ? (isCompleted && counts.checklistSize > 0 ? counts.checklistSize : counts.owned)
      : null,
    missingCount:   tracksProgress
      ? (isCompleted && counts.checklistSize > 0 ? 0 : counts.missing)
      : null,
    duplicateCount: tracksProgress ? counts.duplicate : null,
    checklistSize: counts.checklistSize,
    isCompleted,
    isCollectionOnly: !tracksProgress,
    openAlbumHref: album.id
      ? `/dashboard/my-collection/albums/${album.id}`
      : null,
  };
}


// ═══════════════════════════════════════════════════════════════════
// Phase A — Album Card Title Hierarchy
//
// Presentation-layer normalisation used everywhere the user sees one
// of their albums. The primary card title is ALWAYS the canonical
// collection name (from `catalog_releases.official_title`); the
// secondary line is a short variant descriptor "USA · Hard Cover".
//
// These helpers are intentionally small and framework-agnostic so
// the same rules apply on My Profile → Your Collection, Discover
// Albums → Your Library, and the Open Album page.
// ═══════════════════════════════════════════════════════════════════

// Region codes that MUST stay upper-cased when they appear in a
// variant name (e.g. "USA Hardcover" → "USA · Hard Cover"). We only
// uppercase 2–4 letter codes — full country names like "Qatar" or
// "Mexico" are left in their original Title Case form.
const REGION_TOKENS = new Set([
  "USA", "US", "UK", "EU", "LATAM", "GCC", "MENA",
]);

const _strip = (s) => (s == null ? "" : String(s)).trim();

/**
 * Normalise a variant name into `{ region, cover }` — the two pieces
 * of the customer-facing subtitle. Both fields may be empty.
 *   "USA Hardcover"                     → { region: "USA", cover: "Hard Cover" }
 *   "Gold Hardcover"                    → { region: "Gold", cover: "Hard Cover" }
 *   "2022 Qatar — Soft Cover"           → { region: "Qatar", cover: "Soft Cover" }
 *   "Softcover"                         → { region: "", cover: "Soft Cover" }
 */
function _parseVariantName(name) {
  let text = _strip(name);
  if (!text) return { region: "", cover: "" };

  // Unicode dashes → separator
  text = text.replace(/[\u2013\u2014]/g, "-").replace(/\s+-\s+/g, " · ");

  let cover = "";
  const covers = [
    { rx: /\bhard\s*cover\b/i, out: "Hard Cover" },
    { rx: /\bhardcover\b/i,    out: "Hard Cover" },
    { rx: /\bsoft\s*cover\b/i, out: "Soft Cover" },
    { rx: /\bsoftcover\b/i,    out: "Soft Cover" },
  ];
  for (const c of covers) {
    if (c.rx.test(text)) {
      cover = c.out;
      text = text.replace(c.rx, "").trim();
      break;
    }
  }

  // Strip stray leading year (repeats the year already in the title)
  text = text.replace(/^\s*\d{4}\s+/, "").trim();
  text = text.replace(/^[\s·-]+|[\s·-]+$/g, "").trim();

  const region = text
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (REGION_TOKENS.has(w.toUpperCase()) ? w.toUpperCase() : w))
    .join(" ");

  return { region, cover };
}

/**
 * Primary card title — canonical collection name.
 * Accepts either the enriched `album` (with `album.catalog.release`)
 * or a raw catalog object; falls through to any embedded release
 * fields for compatibility with older card shapes.
 */
export function resolveCollectionTitle(albumOrCatalog) {
  if (!albumOrCatalog) return "Untitled album";
  // `readOfficialTitle` already handles both shapes and includes the
  // WC26 alias mapping, so we reuse it verbatim.
  return readOfficialTitle(albumOrCatalog);
}

/**
 * Secondary card subtitle — "USA · Hard Cover" style, normalised.
 *
 *   • `album.cover_label_override` (e.g. "Other") wins verbatim.
 *   • For editions with no region (e.g. WC 2010), returns just the
 *     cover format: "Soft Cover".
 *   • Never repeats the year (the year lives in the primary title).
 */
export function resolveVariantSubtitle(album, catalog) {
  // Support both call shapes: (album) OR (album, catalog)
  const cat = catalog || album?.catalog || {};
  const override = _strip(album?.cover_label_override);
  if (override) return override;

  const variant = cat.album_variant || album?.album_variant || null;
  const raw = _strip(variant?.name);
  if (!raw) return "";

  const { region, cover } = _parseVariantName(raw);
  if (region && cover) return `${region} · ${cover}`;
  if (region) return region;
  if (cover) return cover;
  return raw;
}

